import type { ClassificationDialogData, ClassificationContentType } from '@core/domain';
import { FEATURE_FLAGS } from '@core/domain';
import { useIsFeatureEnabled } from '@core/hooks';
import { formatTimestamp } from '@core/utils';
import { Badge, Chat, Dialog, Eye, Icon, IconButton, IconButtonSize, IconSize, Photo, Profile, StatusCircleBlock, Volume } from '@soteria/design-system-ve';

import * as styles from './ClassificationDialog.css';
import { V1ContentSection, V1ExplanationSection, V1LabelsSection, V1MetadataSection, V1PoliciesSection } from './ClassificationDialogV1Sections';
import { ClassificationDialogV2Content } from './ClassificationDialogV2';

export interface ClassificationDialogProps {
  /** Classification data to display */
  data: ClassificationDialogData;
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Callback when dialog is closed */
  onClose: () => void;
  /** Whether content should be blurred */
  isContentBlurred: boolean;
  /** Callback to toggle content blur */
  onToggleBlur: () => void;
}

/** Get the appropriate icon for the content type */
function getContentTypeIcon(contentType: ClassificationContentType) {
  switch (contentType) {
    case 'audio':
      return Volume;
    case 'image':
      return Photo;
    case 'username':
      return Profile;
    case 'text':
    case 'mixed':
    default:
      return Chat;
  }
}

/** Get dialog title based on content type */
function getDialogTitle(contentType: ClassificationContentType): string {
  switch (contentType) {
    case 'audio':
      return 'Audio Classification Result';
    case 'image':
      return 'Image Classification Result';
    case 'text':
      return 'Text Classification Result';
    case 'username':
      return 'Username Classification Result';
    case 'mixed':
      return 'Mixed Content Classification Result';
    default:
      return 'Classification Result';
  }
}

/** V1 Content - vertical layout using extracted sections */
function ClassificationDialogV1Content({
  data,
  isContentBlurred,
  onToggleBlur,
}: {
  data: ClassificationDialogData;
  isContentBlurred: boolean;
  onToggleBlur: () => void;
}) {
  const applyBlur = !data.isSafe && isContentBlurred;

  return (
    <div className={styles.container}>
      <V1ContentSection applyBlur={applyBlur} isContentBlurred={isContentBlurred} isSafe={data.isSafe} text={data.text} onToggleBlur={onToggleBlur} />
      <V1ExplanationSection explanation={data.explanation} />
      <V1MetadataSection confidenceScore={data.confidenceScore} duration={data.duration} language={data.language} />
      <V1LabelsSection isSafe={data.isSafe} labels={data.labels} />
      <V1PoliciesSection nonTriggeredPolicies={data.nonTriggeredPolicies} triggeredPolicies={data.triggeredPolicies} />
    </div>
  );
}

/**
 * Shared ClassificationDialog component for displaying classification results.
 *
 * This is a core component that can be used across domains (classification-testing,
 * content-search, moderation). Each domain provides an adapter to convert its
 * domain-specific types to ClassificationDialogData.
 */
export function ClassificationDialog({ data, isOpen, onClose, isContentBlurred, onToggleBlur }: ClassificationDialogProps) {
  const isV2Enabled = useIsFeatureEnabled(FEATURE_FLAGS.CLASSIFICATION_DIALOG_V2);
  const dialogTitle = getDialogTitle(data.contentType);
  const dialogDescription = formatTimestamp(data.timestamp);

  const customHeader = (
    <div>
      <div className={styles.titleContainer}>
        <Icon icon={getContentTypeIcon(data.contentType)} iconSize={IconSize.Small} />
        <h2 className={styles.dialogTitle}>{dialogTitle}</h2>
        {!data.isSafe && (
          <IconButton
            buttonSize={IconButtonSize.Small}
            icon={isContentBlurred ? Eye : StatusCircleBlock}
            iconSize={IconSize.Small}
            title={isContentBlurred ? 'Show content' : 'Hide content'}
            onClick={onToggleBlur}
          />
        )}
      </div>
      {dialogDescription && <p className={styles.dialogDescription}>{dialogDescription}</p>}
    </div>
  );

  // Use 'xxxl' size for V2 wide layout, 'xl' for V1 layout
  const dialogSize = isV2Enabled ? 'xxxl' : 'xl';

  return (
    <Dialog cancelAction={{ label: 'Close' }} customHeader={customHeader} isOpen={isOpen} size={dialogSize} onClose={onClose}>
      <div className={styles.statusBadge}>
        <Badge className={data.isSafe ? styles.safeBadge : styles.unsafeBadge} text={data.isSafe ? 'SAFE' : 'UNSAFE'} />
      </div>

      {isV2Enabled ? (
        <ClassificationDialogV2Content data={data} isContentBlurred={isContentBlurred} onToggleBlur={onToggleBlur} />
      ) : (
        <ClassificationDialogV1Content data={data} isContentBlurred={isContentBlurred} onToggleBlur={onToggleBlur} />
      )}
    </Dialog>
  );
}
